import { useEffect, useRef, useState } from "react";
import { useCropperStore } from "@src/v3.0.0/store/cropper/cropper-store";

export function DraggableOnCropper({ handle, disable, onDrag, onStop, x, y, children }) {
  const dragContainer = useRef();
  const cropper = useCropperStore();
  const [isDragging, setIsDragging] = useState(false);
  // const [position, setPosition] = useState({ x, y });
  const [startClick, setStartClick] = useState({ x: 0, y: 0 });
  const positionRef = useRef({ x, y });
  
  const onMouseMoveOnWindow = (event) => {
    if (!isDragging) return;

    const { cropBox } = cropper.element.cropper;
    const coreBox = dragContainer.current.firstElementChild.getBoundingClientRect();

    const { clientX, clientY } = event;

    // Calcoliamo il movimento del mouse rispetto al primo click (startClick)
    const xMovement = clientX - startClick.x;
    const yMovement = clientY - startClick.y;

    // Aggiungiamo l'offset della posizione iniziale di coreBox, per evitare che il movimento parta da zero
    let newX = startClick.offsetX + xMovement;
    let newY = startClick.offsetY + yMovement;

    const cropBoxRect = cropBox.getBoundingClientRect();

    // Limiti per evitare che coreBox esca da cropBox
    const maxX = cropBoxRect.width - coreBox.width;
    const maxY = cropBoxRect.height - coreBox.height;

    // Assicuriamoci che la nuova posizione non esca dai limiti del cropBox
    newX = Math.max(0, Math.min(newX, maxX));
    newY = Math.max(0, Math.min(newY, maxY));

    // Calcoliamo le percentuali rispetto alla larghezza e altezza del cropBox
    const percentageX = cropBoxRect.width ? (newX / cropBoxRect.width) * 100 : 0;
    const percentageY = cropBoxRect.height ? (newY / cropBoxRect.height) * 100 : 0;

    if (typeof onDrag === 'function') {
      onDrag({ x: percentageX, y: percentageY });
    }

    positionRef.current.x = percentageX;
    positionRef.current.y = percentageY;

    updateDragContainer(percentageX, percentageY);
  };

  const onMouseUpOnWindow = (e) => {
    setIsDragging(false);
    if (typeof onStop === 'function') {
      onStop(onStop, { x: positionRef.current.x, y: positionRef.current.y });
    }
  };

  const onStart = (event) => {
    let parent = event.target;
    while(parent) {
      if (disable.includes(parent.id)) {
        return false;
      }
      parent = parent.parentElement;
    }
    return true;
  }

  const onMouseDownCore = (e) => {
    if (typeof e.button === 'number' && e.button !== 0) return;

    if (Array.isArray(disable) && !onStart(e)) {
      console.log('drag prevented');
      return;
    }

    const { cropBoxData, container } = cropper.element.cropper;
    const coreBox = dragContainer.current.firstElementChild.getBoundingClientRect();
    const cropBoxContainerRect = container.getBoundingClientRect();

    const start = {
      x: e.clientX + cropBoxData.left + cropBoxContainerRect.left,
      y: e.clientY + cropBoxData.top + cropBoxContainerRect.top,
      offsetX: coreBox.left,
      offsetY: coreBox.top,
    };

    setIsDragging(true);
    setStartClick(start);
    e.preventDefault();
  };

  const onTouchMoveOnWindow = (e) => {
    e.preventDefault();
    onMouseMoveOnWindow(e.touches[0]);
  };

  const onTouchEndOnWindow = () => {
    onMouseUpOnWindow();
  };

  const getBoundingPosition = (x, y) => {
    const cropBoxData = cropper.element.cropper.cropBoxData;
    const coreBox = dragContainer.current.firstElementChild.getBoundingClientRect();
    let maxY = Math.max(0, Math.min(y * (cropBoxData.height/100), cropBoxData.height - coreBox.height)) / cropBoxData.height * 100;
    let maxX = Math.max(0, Math.min(x * (cropBoxData.width/100), cropBoxData.width - coreBox.width)) / cropBoxData.width * 100;
    return { maxY, maxX };
  };

  const updateDragContainer = (x, y) => {
    let { maxY, maxX } = getBoundingPosition(x, y);
    const left = `${maxX}%`;
    const top = `${maxY}%`;
    dragContainer.current.firstElementChild.style.left = left;
    dragContainer.current.firstElementChild.style.top = top;
  }

  useEffect(() => {
    if (dragContainer.current) {
      updateDragContainer(x, y);
    }
  }, [x, y]);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', onMouseMoveOnWindow);
      window.addEventListener('mouseup', onMouseUpOnWindow);
      window.addEventListener('touchmove', onTouchMoveOnWindow);
      window.addEventListener('touchend', onTouchEndOnWindow);
      
      return () => {
        window.removeEventListener('mousemove', onMouseMoveOnWindow);
        window.removeEventListener('mouseup', onMouseUpOnWindow);
        window.removeEventListener('touchmove', onTouchMoveOnWindow);
        window.removeEventListener('touchend', onTouchEndOnWindow);
      };
    }
  }, [isDragging]);

  return (
    <div
      ref={dragContainer}
      style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
      onMouseDown={onMouseDownCore}
      onTouchStart={onMouseDownCore} // Aggiunto per supportare il tocco
    >
      {children}
    </div>
  );
}
